import type { RealtimeMessage } from "@dream/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { realtimeUrl } from "../api/client.ts";

/**
 * Conexão de tempo real.
 *
 * O WebSocket é transporte de notificação, **não** fonte de verdade. Duas
 * consequências no código:
 *
 *  1. a mensagem só diz "a entrega X mudou"; o estado real vem de um refetch
 *     contra o PostgreSQL;
 *  2. ao reconectar, sempre há um refetch completo da janela visível, porque
 *     mensagens emitidas durante a queda foram perdidas — NOTIFY não é
 *     persistente.
 *
 * Reconexão com backoff exponencial e jitter. O jitter evita que muitos
 * portais abertos reconectem no mesmo instante depois de um deploy.
 */

export type ConnectionState = "connecting" | "open" | "reconnecting" | "offline";

export interface UseRealtimeOptions {
  /** Credenciais do portal. Ausentes = sessão administrativa por cookie. */
  slug?: string;
  token?: string;
  /** Visão do cliente `/@handle`. A sessão vai no cookie HttpOnly. */
  handle?: string;
  enabled?: boolean;
  /** Chamado a cada notificação de mudança (entrega ou overlay da Agenda). */
  onChange: (
    message: Extract<
      RealtimeMessage,
      { type: "deliverable.changed" } | { type: "google.overlay.changed" }
    >,
  ) => void;
  /** Chamado ao (re)conectar. Deve refazer o fetch da janela visível. */
  onResync: () => void;
}

export interface RealtimeStatus {
  state: ConnectionState;
  channels: string[];
  lastMessageAt: Date | null;
  attempts: number;
}

const MAX_BACKOFF_MS = 30_000;
const HEARTBEAT_MS = 25_000;

export function useRealtime(options: UseRealtimeOptions): RealtimeStatus {
  const { slug, token, handle, enabled = true, onChange, onResync } = options;

  const [state, setState] = useState<ConnectionState>(enabled ? "connecting" : "offline");
  const [channels, setChannels] = useState<string[]>([]);
  const [lastMessageAt, setLastMessageAt] = useState<Date | null>(null);
  const [attempts, setAttempts] = useState(0);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const closedByUs = useRef(false);

  // Refs para os callbacks: sem isso, um componente que recria as funções em
  // cada render derrubaria e reabriria o socket a cada render.
  const onChangeRef = useRef(onChange);
  const onResyncRef = useRef(onResync);
  onChangeRef.current = onChange;
  onResyncRef.current = onResync;

  const clearTimers = useCallback(() => {
    if (reconnectTimer.current !== null) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (heartbeatTimer.current !== null) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;

    closedByUs.current = false;
    setState(attemptRef.current === 0 ? "connecting" : "reconnecting");

    let socket: WebSocket;
    try {
      socket = new WebSocket(realtimeUrl({ slug, token, handle }));
    } catch {
      scheduleReconnect();
      return;
    }

    socketRef.current = socket;

    socket.onopen = () => {
      attemptRef.current = 0;
      setAttempts(0);
      setState("open");

      // Refetch obrigatório na (re)conexão: qualquer mudança ocorrida enquanto
      // a conexão estava caída não gerou mensagem para este cliente.
      onResyncRef.current();

      heartbeatTimer.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, HEARTBEAT_MS);
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      let message: RealtimeMessage;
      try {
        message = JSON.parse(event.data) as RealtimeMessage;
      } catch {
        return;
      }

      setLastMessageAt(new Date());

      if (message.type === "connected") {
        setChannels(message.channels);
        return;
      }

      if (message.type === "deliverable.changed" || message.type === "google.overlay.changed") {
        onChangeRef.current(message);
        return;
      }

      if (message.type === "error") {
        // Erro de autorização não se resolve tentando de novo: o token é
        // inválido, revogado ou expirado.
        closedByUs.current = true;
        setState("offline");
      }
    };

    socket.onclose = () => {
      clearTimers();
      socketRef.current = null;
      if (closedByUs.current) {
        setState("offline");
        return;
      }
      scheduleReconnect();
    };

    socket.onerror = () => {
      // `onclose` sempre vem depois de `onerror`; o reconnect é agendado lá,
      // para não agendar duas vezes.
    };

    function scheduleReconnect(): void {
      if (closedByUs.current || !enabled) return;

      attemptRef.current += 1;
      setAttempts(attemptRef.current);
      setState("reconnecting");

      const base = Math.min(2 ** attemptRef.current * 500, MAX_BACKOFF_MS);
      const jitter = Math.random() * base * 0.3;

      reconnectTimer.current = setTimeout(() => connect(), base + jitter);
    }
  }, [enabled, slug, token, handle, clearTimers]);

  useEffect(() => {
    if (!enabled) {
      setState("offline");
      return;
    }

    connect();

    return () => {
      closedByUs.current = true;
      clearTimers();
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, connect, clearTimers]);

  /**
   * Reconecta na volta da rede e ao reabrir a aba.
   *
   * Um portal deixado aberto num notebook que dormiu volta com o socket morto,
   * mas o navegador só dispara `close` quando algo tenta usá-lo. Estes eventos
   * antecipam a detecção, e o handler de `visibilitychange` também força
   * refetch — a aba pode ter ficado horas sem receber nada.
   */
  useEffect(() => {
    if (!enabled) return;

    const revive = () => {
      const socket = socketRef.current;
      if (socket === null || socket.readyState === WebSocket.CLOSED) {
        attemptRef.current = 0;
        connect();
      } else if (socket.readyState === WebSocket.OPEN) {
        onResyncRef.current();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") revive();
    };

    window.addEventListener("online", revive);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("online", revive);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, connect]);

  return { state, channels, lastMessageAt, attempts };
}
